import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RoutePath } from 'enums/Routes';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useNavigate } from 'hooks/useNavigate';
import { getApplicationData } from 'selectors/getApplicationData';
import { getAvailableShifts, requestShift } from 'thunks';
import { failureNotification } from 'notifications';

const SHIFT_SIGNUP_EVENT = 'shift_signup';
const SHIFT_SLOT_TAKEN_MESSAGE = 'This slot is already taken. Try a new one.';

interface RequestShiftSignupParams {
  shiftRequirementId: number;
  analyticsShiftId?: string;
  redirectTo?: RoutePath;
}

const useRequestShift = () => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const { application } = useSelector(getApplicationData);
  const [isSigningUp, setIsSigningUp] = useState(false);

  const requestShiftSignup = async ({
    shiftRequirementId,
    analyticsShiftId,
    redirectTo = RoutePath.FinancialDashboard,
  }: RequestShiftSignupParams) => {
    const applicationId = application?.id;

    if (!applicationId) return;

    analytics.track(SHIFT_SIGNUP_EVENT, {
      shift_id: analyticsShiftId ?? shiftRequirementId.toString(),
    });
    setIsSigningUp(true);

    try {
      await dispatchWithUnwrap(
        requestShift({
          appId: applicationId,
          shiftRequirementId,
        }),
      );
      dispatch(getAvailableShifts(applicationId));
      navigate(redirectTo, { replace: true });
    } catch {
      dispatch(getAvailableShifts(applicationId));
      failureNotification(SHIFT_SLOT_TAKEN_MESSAGE);
      navigate(redirectTo, { replace: true });
    } finally {
      setIsSigningUp(false);
    }
  };

  return { requestShiftSignup, isSigningUp };
};

export default useRequestShift;
